import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { hasCart, notNull } from 'utils/type_guards';
import { renderModal } from 'utils/modal_util';

import Alert from 'components/shared/Alert';
import Pagination from 'components/shared/Pagination';
import CartProductList from 'components/shared/CartProductList';
import CartHeader from 'components/features/Cart/CartHeader';
import CartSidebar from 'components/features/Cart/CartSidebar';
import ProjectControls from 'components/features/Projects/ProjectControls';
import Spinner from 'components/shared/Spinner';
import Button from 'components/shared/Button';
import ImageGallery from 'components/shared/ImageGallery';
import ColorCorrectOption from 'components/features/Cart/CartSidebar/ColorCorrectOption';
import CheckoutLink from 'components/features/Cart/CheckoutLink';
import Modal from 'components/shared/Modal';
import CartAddOns from 'components/features/Cart/CartSidebar/CartAddOns';

import projects from 'domains/projects';
import photoModule from 'domains/photos';
import cartProductEditor from 'domains/cart_product_editor';
import navigation from 'domains/navigation';
import cartProductsModule from 'domains/cart_products';
import cartModule from 'domains/cart';
import cartBilling from 'domains/cart_billing';

import {
  selectCartLoading,
  selectCartProductsLoading,
  selectCartPageLoading,
} from 'selectors/loading_states';

import type { CartProductMaybeThumb } from 'domains/cart_products';

type CartPageProps = {
  showProjects?: boolean;
};

function CartPage({ showProjects }: CartPageProps) {
  const dispatch = useAppDispatch();
  const cartProductsPage = useAppSelector(cartProductsModule.selectors.page);
  const cartProductsPages = useAppSelector(cartProductsModule.selectors.pageCount);
  const cartLoading = useAppSelector(selectCartLoading);
  const cartProductsLoading = useAppSelector(selectCartProductsLoading);
  const photosById = useAppSelector(photoModule.selectors.selectEntities);
  const canvasConfigPath = useAppSelector(navigation.selectors.canvasConfigPath);
  const cartPageLoading = useAppSelector(selectCartPageLoading);
  const cartProducts = useAppSelector(cartProductsModule.selectors.paginated);
  const betterCart = useAppSelector(cartModule.selectors.cart);
  const billing = useSelector(cartBilling.selectors.billing);
  const editStatus = useAppSelector(cartProductEditor.selectors.status);

  const projectsCount = useAppSelector(projects.selectors.selectTotal);

  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCartImageGallery, setShowCartImageGallery] = useState(false);
  const [cartImageGalleryIndex, setCartImageGalleryIndex] = useState(0);
  const [redirectToReview, setRedirectToReview] = useState(false);
  const [showProjectsList, setShowProjectsList] = useState(showProjects);

  const [showMissingPhotosDialog, setShowMissingPhotosDialog] = useState(false);
  const [pendingEditId, setPendingEditId] = useState<number | null>(null);
  const [missingPhotosMessage, setMissingPhotosMessage] = useState('');

  function handleDeleteCartProduct(id: number) {
    dispatch(cartProductsModule.actions.destroy.pending(id));
  }

  const confirmEmptyCart = () => window.location.assign('/cart/delete');

  useEffect(() => {
    document.body.classList.toggle('hideBranding', localStorage.getItem('hideBranding') === 'true');
  }, []);

  // We want to redirect to the checkout review page if we have the query param for it
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('redirect') === 'review') {
      setRedirectToReview(true);
    }
  }, []);

  useEffect(() => {
    if (editStatus?.status === 'ready') {
      dispatch(navigation.actions.navigateTo('editor'));
    }
  }, [editStatus]);

  function handleContinueOrder() {
    if (canvasConfigPath === '') {
      dispatch(navigation.actions.navigateTo('catalogSelector'));
    } else {
      dispatch(navigation.actions.navigateTo('editor'));
    }
  }

  function handleEditCartProduct(cartProduct: CartProductMaybeThumb) {
    const photoIds = cartProduct.photos.map((p) => p.external_asset_id);
    const missingPhotoIds = photoIds.map((id) => (photosById[id] ? null : id)).filter(notNull);

    if (missingPhotoIds.length > 0) {
      let message = '';
      if (missingPhotoIds.length === 1 && photoIds.length === 1) {
        message = 'The photo used in this item has been deleted.';
      } else if (missingPhotoIds.length > 1 && photoIds.length > 1) {
        message = 'All photos used in this item have been deleted.';
      } else if (missingPhotoIds.length > 1) {
        message = 'Some photos used in this item have been deleted.';
      }

      message += ' Would you like to continue to edit this item and add new photos?';

      setMissingPhotosMessage(message);
      setPendingEditId(cartProduct.id);
      setShowMissingPhotosDialog(true);
    } else {
      dispatch(cartProductEditor.actions.pending(cartProduct.id));
    }
  }

  function handlePagination(page: number) {
    dispatch(cartProductsModule.actions.setPage(page));
  }

  function changeCartProductQuantity(cartProductId: number, quantity: number) {
    dispatch(cartProductsModule.actions.updateQuantity.pending({ id: cartProductId, quantity }));
  }

  function toggleColorCorrect(value: boolean) {
    dispatch(cartModule.actions.update.pending({ color_correct: value }));
  }

  function handleShowCartImageGallery(show: boolean, index: number) {
    setCartImageGalleryIndex(index);
    setShowCartImageGallery(show);
  }

  const cartProductImages = useMemo(() => {
    return cartProducts
      .filter(cartProductsModule.utils.hasPreviewUrl)
      .map((cp) => ({ id: cp.id, src: cp.preview_url }));
  }, [cartProducts]);

  const smallLoading = useMemo(
    () => cartPageLoading && (cartProductsLoading || cartLoading),
    [cartPageLoading, cartProductsLoading, cartLoading]
  );

  if (cartPageLoading) {
    return (
      <div className="cart_page">
        <Spinner color="#fff" size="50" style={{ margin: 'auto' }} />
      </div>
    );
  }

  return (
    <div className="cart_page">
      {(!hasCart(betterCart) || betterCart.product_ids.length === 0) && (
        <div className="cart-page-empty">
          <React.Fragment>
            <h1 className="legend">Your cart is empty.</h1>
            <p className="empty-cart-actions">
              {projectsCount > 0 && (
                <Button
                  type="link"
                  onClick={() => dispatch(navigation.actions.navigateTo('projects'))}
                >
                  Load a Saved Project
                </Button>
              )}
              {projectsCount > 0 && <span> or </span>}
              <Button type="link" onClick={handleContinueOrder}>
                Continue Ordering
              </Button>
            </p>
          </React.Fragment>
        </div>
      )}

      {hasCart(betterCart) && betterCart.product_ids.length > 0 && (
        <React.Fragment>
          <div className="content page">
            <div className="cart_container">
              <CartHeader loading={smallLoading} handleContinueOrder={handleContinueOrder} />
              <div className="pagination-projects-container">
                <Button
                  type="bare"
                  id="empty-cart-button"
                  onClick={() => setShowConfirmDialog(true)}
                >
                  Empty Cart
                </Button>
                {cartProductsPages > 1 && (
                  <Pagination
                    label="cart items pagination"
                    currentPage={cartProductsPage}
                    pageCount={cartProductsPages}
                    onPaginate={handlePagination}
                  />
                )}
                <ProjectControls onShowProjects={() => setShowProjectsList(true)} />
              </div>
              <CartProductList
                cartProducts={cartProducts}
                changeCartProductQuantity={changeCartProductQuantity}
                onDelete={handleDeleteCartProduct}
                onEdit={handleEditCartProduct}
                onShowImageGallery={handleShowCartImageGallery}
              />
              {cartProductsPages > 1 && cartProducts.length > 0 && (
                <Pagination
                  currentPage={cartProductsPage}
                  pageCount={cartProductsPages}
                  onPaginate={handlePagination}
                />
              )}
            </div>
          </div>

          <CartSidebar guid={betterCart.guid} billing={billing}>
            <CheckoutLink loading={loading} redirectToReview={redirectToReview} />
            <ColorCorrectOption
              isChecked={betterCart.color_correct}
              onChange={toggleColorCorrect}
            />
            <CartAddOns />
          </CartSidebar>

          {showCartImageGallery && (
            <ImageGallery
              images={cartProductImages}
              setIsOpen={setShowCartImageGallery}
              startIndex={cartImageGalleryIndex}
            />
          )}
        </React.Fragment>
      )}

      {showConfirmDialog &&
        renderModal(
          <Alert
            title="Confirm Empty Cart"
            labelledBy="confirm-empty-cart-modal"
            lastFocusedElementId="empty-cart-button"
            message="You are about to delete the items in your cart. This can NOT be undone. Are you sure you want to empty your cart?"
            onContinue={confirmEmptyCart}
            onClose={() => setShowConfirmDialog(false)}
            onCancel={() => setShowConfirmDialog(false)}
          />
        )}

      {showMissingPhotosDialog && (
        <Alert
          lastFocusedElementId={`edit-cart-product-${pendingEditId || ''}`}
          title={`Unable to Find Photos`}
          labelledBy="no-edit-cart-product"
          message={missingPhotosMessage}
          onContinue={() => {
            pendingEditId && dispatch(cartProductEditor.actions.pending(pendingEditId));
          }}
          onClose={() => setShowMissingPhotosDialog(false)}
          onCancel={() => setShowMissingPhotosDialog(false)}
        />
      )}

      {editStatus?.status === 'setup' &&
        renderModal(
          <Modal
            labelledBy="edit modal"
            modalClass={['dark', 'edit-cart-product']}
            lastFocusedElementId={`edit-cart-product-${editStatus.id}`}
            title="Edit Cart Product"
            // We do not want this modal to be closable
            onClose={() => {}}
          >
            <Spinner size="30" />
            <p>Preparing item...</p>
          </Modal>
        )}
    </div>
  );
}

export default CartPage;
